import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Grid, Card, CardContent, CircularProgress } from '@mui/material';
import Config from '../../config';
import OAuth from './oauth';

const PROBE_TIMEOUT_MS = 10000;
const STORE_NAME_PROPERTY = 'ShopifyStoreName';

export type InstallResult = { proceed: boolean; problem?: string };

type LegacyOAuthDispatcherProps = {
    system: string;
    legacy: React.ComponentType<any>;
    onInstall?: (shop: string, system: string) => Promise<InstallResult>;
};

type Branch = 'probing' | 'legacy' | 'generic' | 'error';

const matchesSystem = (row: any, system: string) => {
    const target = system.toLowerCase();
    return String(row.System || '').toLowerCase() === target
        || String(row.OAuthName || '').toLowerCase() === target;
};

const loadRow = async (system: string) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        const probe = axios.get(Config.api + '/odata/Company/Functions.GetAllSystemSettings');
        const timeout = new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error('probe timed out')), PROBE_TIMEOUT_MS);
        });
        const res: any = await Promise.race([probe, timeout]);
        return (res.data.value || []).find((r: any) => matchesSystem(r, system));
    } finally {
        if (timer) { clearTimeout(timer); }
    }
};

export const saveShopifyStoreName = async (shop: string, system: string = 'Shopify'): Promise<InstallResult> => {
    const storeName = shop.split('.')[0];
    try {
        await axios.put(
            Config.api + `/odata/Company/Functions.SetSystemSettings?system=${system}`,
            { [STORE_NAME_PROPERTY]: storeName }
        );
    } catch {
        return { proceed: false, problem: STORE_NAME_PROPERTY };
    }
    try {
        const row = await loadRow(system);
        const saved = (row?.Settings || []).find((s: any) => s.Name === STORE_NAME_PROPERTY);
        if (saved === undefined) {
            // AllowedProperties filters on !Internal alone while the row's Settings also
            // require a FriendlyName, so a writable property can be invisible here. Absent
            // is unverifiable, not failed.
            console.warn(`[OAuth] Shopify: could not verify ${STORE_NAME_PROPERTY}; it is not in the connector's settings.`);
            return { proceed: true };
        }
        return saved.Value === storeName
            ? { proceed: true }
            : { proceed: false, problem: STORE_NAME_PROPERTY };
    } catch {
        console.warn(`[OAuth] Shopify: could not re-read ${STORE_NAME_PROPERTY} to verify the write.`);
        return { proceed: true };
    }
};

const spinnerCard = (
    <Grid container spacing={2}>
        <Grid size={{ md: 12 }}>
            <Card>
            <div style={{position: 'relative', marginTop: '1em'}}>
                <CircularProgress variant={'indeterminate'} size='8em'/>
            </div>
            <div style={{padding: '1em'}}>{'Completing authentication...'}</div>
            </Card>
        </Grid>
    </Grid>
);

const LegacyOAuthDispatcher = (props: LegacyOAuthDispatcherProps) => {
    const { system, legacy: LegacyHandler, onInstall } = props;
    const [branch, setBranch] = useState<Branch>('probing');
    const [oauthName, setOAuthName] = useState<string>('');
    const [problem, setProblem] = useState<string | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        const decide = async () => {
            let row: any;
            try {
                row = await loadRow(system);
            } catch {
                if (!cancelled) { setBranch('legacy'); }
                return;
            }
            if (cancelled) { return; }
            if (!row || !row.OAuthName) { setBranch('legacy'); return; }
            if (String(row.OAuthName).toLowerCase() !== String(row.System || '').toLowerCase()) {
                console.error(
                    `[OAuth] ${system}: OAuthName "${row.OAuthName}" does not match System "${row.System}"; using the legacy flow.`
                );
                setBranch('legacy');
                return;
            }

            const params = new URLSearchParams(window.location.search || '');
            const shop = params.get('shop');
            const hasCode = params.has('code');

            if (shop && !hasCode && onInstall) {
                const result = await onInstall(shop, row.System);
                if (cancelled) { return; }
                if (!result.proceed) {
                    setProblem(result.problem);
                    setBranch('error');
                    return;
                }
                window.location.href = `/oauth/${row.OAuthName}`;
                return;
            }

            setOAuthName(row.OAuthName);
            setBranch('generic');
        };
        decide();
        return () => { cancelled = true; };
    }, [system, onInstall]);

    if (branch === 'probing') { return spinnerCard; }
    if (branch === 'error') {
        return <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                <CardContent>
                Authentication failed{problem ? ` — ${problem} could not be saved` : ''}. Please try to connect again from the <Link to='/connections'>settings page</Link>.
                </CardContent>
                </Card>
            </Grid>
        </Grid>;
    }
    if (branch === 'generic') { return <OAuth systemName={oauthName} />; }
    return <LegacyHandler />;
};

export default LegacyOAuthDispatcher;
